import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setDeck } from './actions';
import { getDecks } from '../Decks/actions';
import { selectDecksLoaded, selectPlayableDecks } from '../Decks/selectors';
import { selectDeckPile, selectSettingDeck } from './selectors';
import styled from 'styled-components';
import { Button, Muted, PlainButton } from '../Controls';
import { deckBoxUrl } from '../services/helpers';
import { getAllCards } from '../Cards/actions';
import { selectAllCards } from '../Cards/selectors';
import { EnergySymbol } from '../Energy';
import { deckEnergyTypes } from '../DecksPage/DeckPanel';
import DeckSheet from './DeckSheet';

/** A deck's energy types under its name. */
const Energies = styled.span`
  display: flex;
  gap: 2px;
  justify-content: center;
  min-height: 14px;
`;

/** The decks side by side, each its box and name, wrapping onto more rows when they don't fit. */
const Choices = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  padding: 4px 0 6px;
`;

const Choice = styled(PlainButton)`
  flex: 0 0 auto;
  width: 84px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 4px;
  border-radius: 10px;
  border: 2px solid ${({ $chosen }) => ($chosen ? '#f6c945' : 'transparent')};
  background: ${({ $chosen }) => ($chosen ? 'rgba(246, 201, 69, .14)' : 'transparent')};
  font-size: 12px;
  line-height: 1.2;
  text-align: center;

  /* The box picture, not the energy symbols under the name. */
  & > img {
    height: 88px;
    max-width: 100%;
    object-fit: contain;
  }
`;

const SetDeck = ({ userId }) => {
  const dispatch = useDispatch();
  const deckPile = useSelector((state) => selectDeckPile(state, { userId }));
  const decks = useSelector(selectPlayableDecks);
  const decksLoaded = useSelector(selectDecksLoaded);
  const setting = useSelector(selectSettingDeck);
  const [deckId, changeDeckId] = useState('');
  // Every card, for the decks' energy and for opening one to see its cards.
  const cardsById = useSelector(selectAllCards);
  const [seeing, setSeeing] = useState(false);
  const chosen = decks.find(deck => deck.get('id') === deckId);

  useEffect(() => {
    if (decks.size) {
      changeDeckId(decks.first().get('id'));
    }
  }, [decks]);

  useEffect(() => {
    dispatch(getDecks());
    dispatch(getAllCards());
  }, [dispatch]);

  if (deckPile) {
    return null;
  }

  if (!decksLoaded) {
    return <Muted>Loading decks…</Muted>;
  }

  return (
    <>
    <form
      onSubmit={(e) => { e.preventDefault(); dispatch(setDeck(deckId)); }}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}
    >
      <Choices role="radiogroup" aria-label="Deck">
        {decks.map((deck) => (
          <Choice
            key={deck.get('id')}
            type="button"
            role="radio"
            aria-checked={deck.get('id') === deckId}
            $chosen={deck.get('id') === deckId}
            onClick={() => changeDeckId(deck.get('id'))}
          >
            {deckBoxUrl(deck) && <img src={deckBoxUrl(deck)} alt="" />}
            {deck.get('name')}
            {cardsById && (
              <Energies aria-label="Energy">
                {deckEnergyTypes(deck, cardsById).map(type => <EnergySymbol key={type} type={type} size="14px" />)}
              </Energies>
            )}
          </Choice>
        ))}
      </Choices>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="button" onClick={() => setSeeing(true)} disabled={!chosen || !cardsById}>See this deck</Button>
        <Button $primary disabled={setting}>{setting ? 'Shuffling…' : 'Use this deck'}</Button>
      </div>
    </form>
    {/* Outside the form: its buttons mustn't submit it (choose the deck). */}
    {seeing && chosen && cardsById && <DeckSheet deck={chosen} cardsById={cardsById} onClose={() => setSeeing(false)} />}
    </>
  );
};

SetDeck.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default SetDeck;
